import logoImage from "@/assets/logo.png";
import { Card, CardContent } from "@/components/ui/card";
import { HeroSection } from "@/components/HeroSection";
import { ClubAccessRequestModal } from "@/components/ClubAccessRequestModal";
import { LogIn, Building2, Mail } from "lucide-react";
import { SiGoogle, SiApple } from "react-icons/si";

const BG = "#0d1a0a";
const GREEN = "#325e22";
const GREEN_LIGHT = "#6fcf54";
const CREAM = "#f4f9f2";
const CREAM2 = "#e8f4e4";

export function LandingPage() {
  const handleLogin = (userType: "player" | "club") => {
    window.location.href = `/api/login/${userType}`;
  };

  return (
    <div className="min-h-screen" style={{ background: BG }}>
      {/* ─── NAV ─── */}
      <header
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 lg:px-16"
        style={{
          height: 64,
          background: "rgba(13,26,10,0.7)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          paddingTop: "max(0px, env(safe-area-inset-top, 0px))",
        }}
      >
        <div className="flex items-center gap-2.5">
          <img src={logoImage} alt="CourtVibe" className="h-8 w-8 rounded-lg" />
          <span className="font-black text-lg text-white" style={{ letterSpacing: -0.5 }}>
            CourtVibe
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleLogin("player")}
            className="hidden sm:block text-sm font-semibold text-white rounded-xl transition-colors hover:bg-white/10"
            style={{
              padding: "8px 18px",
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.18)",
              whiteSpace: "nowrap",
            }}
            data-testid="button-header-login"
          >
            Iniciar sesión
          </button>
          <a
            href="/api/download"
            className="text-sm font-bold rounded-xl transition-opacity hover:opacity-90"
            style={{ padding: "8px 18px", background: GREEN_LIGHT, color: BG, textDecoration: "none", whiteSpace: "nowrap" }}
          >
            Descargar app
          </a>
        </div>
      </header>

      <main style={{ paddingTop: 64 }}>
        {/* ─── HERO ─── */}
        <HeroSection
          onGetStarted={() =>
            document.getElementById("join-section")?.scrollIntoView({ behavior: "smooth" })
          }
        />

        {/* ─── JOIN SECTION — CREMA ─── */}
        <section id="join-section" style={{ background: CREAM, padding: "80px 60px" }}>
          <h2
            className="text-center font-black mb-3"
            style={{ fontSize: 32, letterSpacing: -1, color: BG }}
          >
            Únete a CourtVibe
          </h2>
          <p className="text-center mb-12 max-w-xl mx-auto" style={{ color: "#6b7a65", fontSize: 16 }}>
            Elige cómo quieres usar la plataforma. Regístrate con Google, Apple o Replit.
          </p>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Jugador */}
            <Card
              className="overflow-visible"
              style={{ background: "#fff", border: `1.5px solid ${CREAM2}`, boxShadow: "0 4px 24px rgba(50,94,34,0.08)", borderRadius: 20 }}
            >
              <CardContent className="pt-8 pb-8 text-center">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
                  style={{ background: "rgba(50,94,34,0.1)", border: "1px solid rgba(50,94,34,0.15)" }}
                >
                  <span className="text-3xl">👤</span>
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: BG }}>Soy Jugador</h3>
                <p className="text-sm mb-6" style={{ color: "#7a8c75", lineHeight: 1.6 }}>
                  Registra partidos, sube en el ranking, únete a ligas y conecta con otros jugadores de pádel.
                </p>
                <button
                  onClick={() => handleLogin("player")}
                  className="w-full text-sm font-bold text-white rounded-xl py-3 mb-5 transition-opacity hover:opacity-90"
                  style={{ background: GREEN, boxShadow: "0 4px 16px rgba(50,94,34,0.3)" }}
                  data-testid="button-login-player"
                >
                  Registrarme como Jugador
                </button>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px" style={{ background: CREAM2 }} />
                  <span className="text-xs" style={{ color: "#aab5a5" }}>Opciones de acceso</span>
                  <div className="flex-1 h-px" style={{ background: CREAM2 }} />
                </div>
                <div className="flex justify-center gap-5">
                  {[
                    [Mail, "Email"],
                    [SiGoogle, "Google"],
                    [SiApple, "Apple"],
                  ].map(([Icon, name]) => (
                    <div key={name as string} className="flex items-center gap-1.5 text-xs" style={{ color: "#9aab95" }}>
                      <Icon className="h-3.5 w-3.5" />
                      <span>{name as string}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Club */}
            <Card
              className="overflow-visible"
              style={{ background: "#fff", border: `1.5px solid ${CREAM2}`, boxShadow: "0 4px 24px rgba(50,94,34,0.08)", borderRadius: 20 }}
            >
              <CardContent className="pt-8 pb-8 text-center">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
                  style={{ background: "rgba(50,94,34,0.1)", border: "1px solid rgba(50,94,34,0.15)" }}
                >
                  <span className="text-3xl">🏟️</span>
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: BG }}>Soy Club</h3>
                <p className="text-sm mb-6" style={{ color: "#7a8c75", lineHeight: 1.6 }}>
                  Gestiona tus pistas, organiza torneos, conecta con jugadores y haz crecer tu comunidad.
                </p>
                <ClubAccessRequestModal
                  trigger={
                    <button
                      className="w-full text-sm font-bold rounded-xl py-3 mb-5 transition-colors hover:bg-primary/5"
                      style={{
                        color: GREEN,
                        border: `1.5px solid ${GREEN}`,
                        background: "transparent",
                      }}
                      data-testid="button-club-access"
                    >
                      <Building2 className="h-4 w-4 inline mr-2" />
                      Solicitar Acceso para Club
                    </button>
                  }
                />
                <p className="text-xs" style={{ color: "#aab5a5", lineHeight: 1.5 }}>
                  Los registros de clubes son gestionados manualmente por nuestro equipo para garantizar la calidad del servicio.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ─── FEATURES — BLANCO ─── */}
        <section id="features" style={{ background: "#ffffff", padding: "80px 60px" }}>
          <div className="text-center mb-12">
            <div
              className="inline-flex items-center gap-2 rounded-full text-xs font-bold uppercase tracking-wide mb-4"
              style={{
                background: "rgba(50,94,34,0.08)",
                border: "1px solid rgba(50,94,34,0.18)",
                padding: "5px 14px",
                color: GREEN,
                letterSpacing: "0.05em",
              }}
            >
              Funcionalidades
            </div>
            <h2 className="font-black mb-3" style={{ fontSize: 32, letterSpacing: -1, color: BG }}>
              Todo lo que Necesitas para Dominar la Pista
            </h2>
            <p style={{ color: "#7a8c75", fontSize: 15 }}>
              Herramientas pensadas para jugadores serios.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              {
                icon: "🎯",
                title: "Rankings Basados en ELO",
                desc: "Sigue tu nivel de habilidad con nuestro sistema de ranking avanzado inspirado en los ratings ELO de ajedrez.",
                comingSoon: false,
              },
              {
                icon: "🏆",
                title: "Crea y únete a Ligas",
                desc: "Crea y gestiona tus propias ligas con reglas personalizadas y membresía solo por invitación.",
                comingSoon: false,
              },
              {
                icon: "👥",
                title: "Encuentra Jugadores",
                desc: "Conecta con jugadores de tu nivel. Haz amigos y encuentra oponentes dignos.",
                comingSoon: false,
              },
              {
                icon: "📈",
                title: "Sigue tu Progreso",
                desc: "Visualiza tu mejora a lo largo del tiempo con gráficos de rendimiento detallados y estadísticas.",
                comingSoon: false,
              },
              {
                icon: "⚔️",
                title: "Registra Partidos",
                desc: "Registra tus partidos y mira cómo tus rankings se actualizan en tiempo real. Cada punto cuenta.",
                comingSoon: false,
              },
              {
                icon: "🏅",
                title: "Torneos y Americanos",
                desc: "Organiza torneos y cuadros americanos dentro de tu liga. Brackets automáticos y resultados en tiempo real.",
                comingSoon: true,
              },
            ].map((f) => (
              <div
                key={f.title}
                className="relative rounded-2xl p-7"
                style={{
                  background: f.comingSoon ? "#f9f9f9" : CREAM,
                  border: `1px solid ${f.comingSoon ? "#e5e5e5" : CREAM2}`,
                  boxShadow: "0 2px 12px rgba(50,94,34,0.06)",
                  opacity: f.comingSoon ? 0.85 : 1,
                }}
              >
                {f.comingSoon && (
                  <span
                    className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-wide rounded-full"
                    style={{
                      padding: "3px 10px",
                      background: "rgba(50,94,34,0.1)",
                      border: "1px solid rgba(50,94,34,0.2)",
                      color: GREEN,
                      letterSpacing: "0.05em",
                    }}
                  >
                    Próximamente
                  </span>
                )}
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 text-2xl"
                  style={{
                    background: f.comingSoon ? "rgba(0,0,0,0.05)" : "rgba(50,94,34,0.1)",
                    border: `1px solid ${f.comingSoon ? "rgba(0,0,0,0.08)" : "rgba(50,94,34,0.12)"}`,
                    filter: f.comingSoon ? "grayscale(0.4)" : "none",
                  }}
                >
                  {f.icon}
                </div>
                <h3 className="font-bold mb-2" style={{ fontSize: 15, letterSpacing: -0.2, color: f.comingSoon ? "#6b7a65" : BG }}>
                  {f.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: "#7a8c75" }}>
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ─── CTA — OSCURO ─── */}
        <section
          className="text-center"
          style={{
            background: BG,
            padding: "80px 60px",
            borderTop: "1px solid rgba(111,207,84,0.1)",
          }}
        >
          <h2
            className="font-black text-white mb-4"
            style={{ fontSize: 44, letterSpacing: -2, lineHeight: 1.05 }}
          >
            ¿Listo para Elevar
            <br />
            tu Juego?
          </h2>
          <p className="mb-10 mx-auto max-w-lg text-lg leading-relaxed" style={{ color: "rgba(255,255,255,0.45)" }}>
            Únete a jugadores que ya están siguiendo su progreso y escalando posiciones.
          </p>
          <button
            onClick={() =>
              document.querySelector("[data-testid='button-login-player']")?.scrollIntoView({ behavior: "smooth" })
            }
            className="inline-flex items-center gap-2 text-base font-black rounded-2xl transition-opacity hover:opacity-90"
            style={{
              padding: "16px 40px",
              background: GREEN_LIGHT,
              color: BG,
              boxShadow: "0 8px 32px rgba(111,207,84,0.25)",
              letterSpacing: -0.3,
            }}
            data-testid="button-join-now"
          >
            Únete Ahora — Es Gratis →
          </button>
          <p className="mt-5 text-xs" style={{ color: "rgba(255,255,255,0.25)" }}>
            Al continuar aceptas nuestros{" "}
            <a href="/terminos" className="underline hover:opacity-75" style={{ color: "rgba(255,255,255,0.45)" }} data-testid="link-landing-terms">
              Términos
            </a>{" "}
            y la{" "}
            <a href="/privacidad" className="underline hover:opacity-75" style={{ color: "rgba(255,255,255,0.45)" }} data-testid="link-landing-privacy">
              Política de Privacidad
            </a>
            .
          </p>
        </section>

        {/* ─── FOOTER ─── */}
        <footer
          className="flex flex-wrap justify-center gap-x-6 gap-y-2 py-7 px-6"
          style={{ borderTop: "1px solid rgba(255,255,255,0.07)", background: BG }}
        >
          {(
            [
              ["/terminos", "Términos", "link-footer-terms"],
              ["/privacidad", "Privacidad", "link-footer-privacy"],
              ["/terms", "Terms (EN)", undefined],
              ["/privacy", "Privacy (EN)", undefined],
              ["mailto:contacto@courtvibe.app", "contacto@courtvibe.app", undefined],
            ] as [string, string, string | undefined][]
          ).map(([href, label, testId]) => (
            <a
              key={label}
              href={href}
              className="text-xs underline hover:opacity-75 transition-opacity"
              style={{ color: "rgba(255,255,255,0.3)" }}
              data-testid={testId}
            >
              {label}
            </a>
          ))}
        </footer>
      </main>
    </div>
  );
}
